import { useEffect } from 'react'
import { useCookies } from 'react-cookie'

import { useModerator } from 'hooks/adFree'

import { getDomain } from 'services/cookie.service'
import { Option } from 'components/elements/ArchidektDropdown/types'

import PhatDropdown from 'components/formElements/PhatDropdown'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'

import styles from './themeDropdown.module.scss'

export type Theme = 'light' | 'dark' | 'test'

export const THEME_CLASS_NAMES: Record<Theme, string> = {
  light: '',
  dark: 'dark-mode',
  test: 'oled',
}

type Props = { className?: string; fullDropdown?: boolean; up?: boolean }

const ThemeDropdown = ({ className, fullDropdown, up }: Props) => {
  const [theme, setTheme, systemThemeEnabled] = useTheme()
  const isMod = useModerator()

  const options: Option[] = [
    {
      icon: 'sun',
      label: 'Light theme',
      onClick: () => setTheme('light'),
      className: theme === 'light' && !systemThemeEnabled ? styles.selected : undefined,
      id: 'light',
    },
    {
      icon: 'moon',
      label: 'Dark theme',
      onClick: () => setTheme('dark'),
      className: theme === 'dark' && !systemThemeEnabled ? styles.selected : undefined,
      id: 'dark',
    },
    {
      icon: 'flask',
      label: 'Test theme',
      onClick: () => setTheme('test'),
      className: theme === 'test' && !systemThemeEnabled ? styles.selected : undefined,
      hidden: !isMod,
      id: 'test',
    },
    { type: 'spacer' },
    {
      icon: 'circle-half-stroke',
      label: 'System theme',
      onClick: () => setTheme(),
      className: systemThemeEnabled ? styles.selected : undefined,
      id: 'system',
    },
  ]

  if (fullDropdown)
    return (
      <PhatDropdown
        className={className}
        menuClassName={up ? styles.mobileMenu : undefined}
        header="Site theme"
        value={systemThemeEnabled ? 'system' : theme}
        options={options}
      />
    )

  return (
    <ArchidektDropdown triggerClassName={className} menuClassName={styles.menu} triggerIcon="moon" options={options} />
  )
}

export default ThemeDropdown

export const useTheme = (): [theme: Theme, setTheme: (value?: Theme) => void, systemThemeEnabled: boolean] => {
  const [{ tbTheme: tbTheme, tbSystemTheme: usingSystemTheme }, setCookies] = useCookies(['tbTheme', 'tbSystemTheme'])

  const systemThemeEnabled = usingSystemTheme === 'true' // Returns boolean if system theme is set
  const actualTheme = getThemeName(tbTheme) // Ensures that we're returning a clean Theme type

  const handleSetTheme = (theme?: Theme) => {
    const themeToCache = getThemeName(theme)

    setCookies('tbTheme', themeToCache, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) })
    setCookies('tbSystemTheme', !theme, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) })

    const themeClassName = THEME_CLASS_NAMES[themeToCache]

    const removedClassNames: string[] = Object.keys(THEME_CLASS_NAMES)
      .filter(themeKey => themeKey !== themeToCache)
      // @ts-ignore
      .map(themeKey => THEME_CLASS_NAMES[themeKey])

    for (const className of removedClassNames) {
      if (!className) continue

      document.body.classList.remove(className)
    }

    if (themeClassName) document.body.classList.add(themeClassName)
  }

  useEffect(() => {
    handleSetTheme(systemThemeEnabled ? undefined : tbTheme)
  }, [])

  return [actualTheme, handleSetTheme, systemThemeEnabled]
}

export const getThemeName = (theme: any): Theme => {
  // @ts-ignore - we recognized the cached theme name, return it
  if (typeof THEME_CLASS_NAMES[theme] === 'string') return theme

  // User's browser prefers dark theme
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'

  return 'light'
}
